import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@utils/i18n/i18n';
import { useSelector, useDispatch } from 'react-redux';
import { faArrowRotateLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import Button from '@App/components/Button/Button';
import Select from '@App/components/Select/Select';
import VehicleTypeSelect from '@App/components/VehicleTypeSelect/VehicleTypeSelect';
import { useStations, useVehicles, useAvailableCommunes } from '@services/query.service';
import {
  filterVehicleTypes, filterVehiclePropulsion, filterVehicleTransmission, filterVehicleProvider,
  filterStationVehicleTypes, filterStationPropulsion, filterStationTransmission, 
  filterStationProvider, isCargoBikeStation, hasAvailableCargoBikes, prepStations,
  downloadData, getAllVehicleTypes,
  useTransmissionOptions, usePropulsionOptions,
} from './sidepanel.utils';
import {
  resetFilter, setProviders, setVehicleTypes, setTransmission, setPropulsion, setCommune,
} from '../map.slice';
import { providersOptions } from './sidepanel.constants';

import './sidepanel.styl';

function Sidepanel({
  sidepanelIsOpen,
  setSidepanelIsOpen,
  setVehicles,
  setStations,
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const filterHasInitialState = useSelector(state => state.map.filterHasInitialState);
  const providers = useSelector(state => state.map.filter.providers);
  const vehicleTypes = useSelector(state => state.map.filter.vehicleTypes);
  const transmission = useSelector(state => state.map.filter.transmission);
  const propulsion = useSelector(state => state.map.filter.propulsion);
  const commune = useSelector(state => state.map.filter.commune);
  const showEmptyStations = useSelector(state => state.map.filter.showEmptyStations);

  const transmissionOptions = useTransmissionOptions();
  const propulsionOptions = usePropulsionOptions();

  const { data: vehicles, isLoading: vehiclesAreLoading } = useVehicles();
  const { data: stations, isLoading: stationsAreLoading } = useStations();
  const { data: communes } = useAvailableCommunes();

  const [downloadInitiated, setDownloadInitiated] = useState();
  const [availablePropulsionTypes, setAvailablePropulsionTypes] = useState([]);
  const [availableTransmissionTypes, setAvailableTransmissionTypes] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);

  const availableFilters = useMemo(() => {
    // remove filters that are not relevant for data we got
    const allVehicleTypes = getAllVehicleTypes(vehicles, stations);

    return { 
      availableVehicleTypes: Array.from(new Set(allVehicleTypes.map(d => d.form_factor))), 
      availablePropulsionTypes: Array.from(new Set(allVehicleTypes.map(d => d.propulsion_type))),
    };
  }, [vehicles, stations]);

  useEffect(
    () => {
      if (!vehicles || !stations) return;
      let fVehicles = filterVehicleTypes(vehicles, vehicleTypes);
      let fStations = filterStationVehicleTypes(stations, vehicleTypes);

      let allVehicleTypes = getAllVehicleTypes(fVehicles, fStations);
      setAvailablePropulsionTypes(Array.from(new Set(allVehicleTypes.map(d => d.propulsion_type))));

      fVehicles = filterVehiclePropulsion(fVehicles, propulsion);
      fStations = filterStationPropulsion(fStations, propulsion);

      allVehicleTypes = getAllVehicleTypes(fVehicles, fStations);
      setAvailableTransmissionTypes(
        Array.from(new Set(allVehicleTypes.flatMap(d => d.vehicle_accessories))),
      );

      fVehicles = filterVehicleTransmission(fVehicles, transmission);
      fStations = filterStationTransmission(fStations, transmission);

      const hasCargoBikeFilter = vehicleTypes.includes('cargo_bicycle');
      const hasAvailableCargoBike = hasAvailableCargoBikes(fVehicles, fStations);
      const availableOperators = Array.from(new Set([
        ...fVehicles.map(d => d?.properties?.operator),
        ...fStations
          .filter(s => s?.properties?.vehicles?.length
            || (showEmptyStations && hasCargoBikeFilter && hasAvailableCargoBike
              && isCargoBikeStation(s)))
          .map(s => s?.properties?.operator),
      ]));
      setAvailableProviders(availableOperators);

      fVehicles = filterVehicleProvider(fVehicles, providers);
      fStations = filterStationProvider(fStations, providers);

      if (commune) {
        fVehicles = fVehicles.filter(d => d?.properties?.commune === commune.id);
        fStations = fStations.filter(s => s?.properties?.commune === commune.id);
      }

      const hasAvailableCargoBikeOnMap = hasAvailableCargoBikes(fVehicles, fStations);
      setVehicles(fVehicles);
      setStations(prepStations(
        fStations,
        showEmptyStations,
        vehicleTypes,
        hasAvailableCargoBikeOnMap,
      ));
    },
    [
      vehicles, stations, vehicleTypes, providers, transmission, propulsion, 
      commune, showEmptyStations, dispatch, setVehicles, setStations,
    ],
  );

  const availablePropulsionOptions = propulsionOptions
    .filter(o => availableFilters.availablePropulsionTypes.includes(o.id));

  return (
    <div 
      className={`sidepanel${sidepanelIsOpen ? '' : ' sidepanel--closed'}`}
    >
      <div className="sidepanel__header">
        <h3 className="sidepanel__header__title">{t('filter_title')}</h3>
        <div className="sidepanel__header__buttons">
          {!filterHasInitialState && (
            <Button 
              className="sidepanel__header__buttons__button"
              onClick={() => dispatch(resetFilter())}
              icon={faArrowRotateLeft}
              rootElProps={{ title: t('filter_reset_button_tooltip') }}
              layout="ghost"
            />
          )}
          <Button 
            className="sidepanel__header__buttons__button"
            onClick={() => setSidepanelIsOpen(false)}
            icon={faChevronRight}
            rootElProps={{ title: t('filter_close_button_tooltip') }}
            layout="ghost"
          />
        </div>
      </div>
      <div className={`sidepanel__form${vehiclesAreLoading || stationsAreLoading ? ' is-loading' : ''}`}>
        <div className="sidepanel__form__item">
          <span className="sidepanel__form__item__label">
            {t('filter_transport-type_input_label')}
          </span>
          <VehicleTypeSelect 
            vehicleTypes={vehicleTypes}
            availablevVehicleTypes={availableFilters?.availableVehicleTypes || []}
            setVehicleTypes={v => dispatch(setVehicleTypes(v))}
          />
        </div>
        {/* <div className="sidepanel__form__item">
          <Toggle
            name="show-empty-stations"
            checked={showEmptyStations}
            label={t('filter_show-empty-stations')}
            onChange={(e, isChecked) => dispatch(setShowEmptyStations(isChecked))}
          />
        </div> */}
        <div className="sidepanel__form__item">
          <div className="sidepanel__form__item__label">
            <span>{t('filter_propulsion_input_label')}</span>
            <Button 
              layout="ghost" 
              onClick={() => {
                dispatch(setPropulsion(propulsion.length 
                  === availablePropulsionOptions.length ? [] : availablePropulsionOptions));
              }}
              className="sidepanel__form__item__label__clear"
            >
              {propulsion.length === availablePropulsionOptions.length ? t('filter_deselect-all') : t('filter_select-all')}
            </Button>
          </div>
          <div className="sidepanel__form__item__options">
            {availablePropulsionOptions
              .map(({ label, id }) => {
                const selected = propulsion.map(to => to.id).includes(id);
        
                return (
                  <Button
                    className="sidepanel__form__item__options__option"
                    key={id}
                    onClick={() => {
                      if (selected) {
                        dispatch(setPropulsion(propulsion.filter(to => to.id !== id)));
                      } else dispatch(setPropulsion([...propulsion, { label, id }]));
                    }}
                    disabled={!availablePropulsionTypes.includes(id)}
                    rootElProps={{ 'data-selected': selected }}
                  >
                    {label}
                  </Button>
                );
              })}
          </div>
        </div>
        {vehicleTypes && (
          <div className="sidepanel__form__item">
            <span className="sidepanel__form__item__label">
              {t('filter_transmission_input_label')}
            </span>
            <div className="sidepanel__form__item__options">
              {transmissionOptions.map(({ label, id }) => {
                const selected = transmission.map(to => to.id).includes(id);
      
                return (
                  <Button
                    className="sidepanel__form__item__options__option"
                    key={id}
                    onClick={() => {
                      if (selected) {
                        dispatch(setTransmission(transmission.filter(to => to.id !== id)));
                      } else dispatch(setTransmission([...transmission, { label, id }]));
                    }}
                    disabled={!availableTransmissionTypes.includes(id)}
                    rootElProps={{ 'data-selected': selected }}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}
        <div className="sidepanel__form__item">
          <div className="sidepanel__form__item__label">
            <span>{t('filter_provider_input_label')}</span>
            <Button 
              layout="ghost"
              onClick={() => {
                dispatch(setProviders(providers.length 
                  === providersOptions.length ? [] : providersOptions));
              }}
              className="sidepanel__form__item__label__clear"
            >
              {providers.length 
                  === providersOptions.length ? t('filter_deselect-all') : t('filter_select-all')}
            </Button>
          </div>
          <div className="sidepanel__form__item__providers">
            {providersOptions.map(({ label, id }) => {
              const selected = providers.map(p => p.id).includes(id);

              return (
                <Button
                  className="sidepanel__form__item__providers__provider"
                  key={id}
                  icon={`/img/icons/providers/${id}.svg`}
                  onClick={() => {
                    if (selected) {
                      dispatch(setProviders(providers.filter(p => p.id !== id)));
                    } else dispatch(setProviders([...providers, { label, id }]));
                  }}
                  disabled={!availableProviders.includes(id)}
                  rootElProps={{ 'data-selected': selected }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </div>
        {pathname?.includes('/download') && (
          <label className="sidepanel__form__item">
            <span className="sidepanel__form__item__label">
              {t('filter_municipality_input_label')}
            </span>
            <Select
              onInputChange={v => dispatch(setCommune(v))}
              options={communes || []}
              value={commune}
              className="sidepanel__form__item__input"
            />
          </label>
        )}
      </div>
      {pathname?.includes('/download') && !vehiclesAreLoading 
        && !stationsAreLoading && (
        <Button 
          className="sidepanel__download"
          onClick={async () => {
            setDownloadInitiated(true);
            await downloadData(
              vehicleTypes, 
              providers, 
              transmission, 
              propulsion, 
              commune,
            );
            setDownloadInitiated(false);
          }}
          disabled={downloadInitiated}
          isLoading={downloadInitiated}
        >
          {t('filter_download')}
        </Button>
      )}
    </div>
  );
}

export default Sidepanel;
